import { ReactNode, useContext, useState } from 'react'
import React from 'react'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tooltip from '@qc/particle/lib/components/tooltip'

import HELP_TEXT from '../../../constants/helpText'
import { Account } from '../../../models'
import { Application, HeaderTitle, Suite } from '../../../suite/common'

import GlobalNavPopupContext, { PopupType } from '../../../PopupContext'
import { Dropdown } from '../../Dropdown'
import styles from './ApplicationDropdown.scss'

export interface ApplicationDropdownProps {
  account: Account
  currentApp: Application
  appSuite: Suite
  buildUrl: (app: Application, account: Account) => string
  enabledApps: string[]
}

export interface ApplicationDropdownState {
  helpText: string
}

interface SectionItemProps {
  dataSection: string,
  app: Application,
  productTitle: string,
  enabled: boolean,
  account: Account,
  isCurrent: boolean
  buildUrl: (app: Application, account: Account) => string,
  onMouseEnter?: React.MouseEventHandler,
  onMouseLeave?: React.MouseEventHandler
}

const SectionItem = ({
  dataSection,
  app,
  productTitle,
  enabled,
  account,
  isCurrent,
  buildUrl,
  onMouseEnter,
  onMouseLeave
}: SectionItemProps) => {
  const activeClass = isCurrent ? styles.active : ''

  if (!enabled) {
    return (
      <Tooltip
        title={<>
          <span>This application is not enabled for this account.</span>
        </>}
        transitionName={null}
      >
        <div
          className={`${styles.nav__products__links__item} ${styles.disabled}`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <span className={activeClass}>{productTitle}</span>
        </div>
      </Tooltip>
    )
  }

  return (
      <a
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-section={dataSection}
        data-cy={Application[app]}
        className={styles.nav__products__links__item}
        href={buildUrl(app, account)}
      >
        <span className={activeClass}>{productTitle}</span>
      </a>)
}

const HeaderSection = ({ productHeader, icon }: {productHeader: HeaderTitle, icon: IconProp}) => {
  return (
    <div data-cy={productHeader} className={styles.nav__products__header} key={productHeader}>
      <span>
        <FontAwesomeIcon icon={icon} size="1x" />
        {productHeader}
      </span>
    </div>
  )
}

const ApplicationDropdown = ({ appSuite, enabledApps, account, currentApp, buildUrl }: ApplicationDropdownProps) => {
  const [helpText, setHelpText] = useState(HELP_TEXT.DEFAULT)
  const popupContext = useContext(GlobalNavPopupContext)
  const isOpen = popupContext.openPopup === PopupType.PRODUCT

  const isAppEnabled = (app) => {
    return appSuite.REQUIRED_APP_FLAGS[app] === undefined || enabledApps.indexOf(appSuite.REQUIRED_APP_FLAGS[app]) > -1
  }

  return (
    <Dropdown
      actionIcon={(
        <div
          data-cy="toggleProductDropdown"
          className={`${styles.nav__product__selector} ${
            isOpen
              ? styles.nav__product__selector__selected
              : ''
            }`}
        >
          <span data-cy="productName" className="nav__product__name">
            {appSuite.APP_NAME_MAPPING[currentApp]}
          </span>
          <div className="nav__product__arrow_container">
            <FontAwesomeIcon
              icon={faChevronDown}
              className={styles.chevron}
              color="nimbus"
            />
          </div>
        </div>
      )}
      type={PopupType.PRODUCT}
      offsetType="left"
      width={600}
      offset={160}
    >
      <div data-cy="navProducts" className={styles.nav__products}>
        <section>
          {appSuite.HEADER_LIST.map((header) =>
            <HeaderSection key={header} productHeader={header} icon={appSuite.HEADER_ICON_MAPPING[header]} />
          )}
        </section>

        <section>
          {appSuite.HEADER_LIST.map(header => {
            return (
              <div className={styles.nav__products__links} key={`${header}_product_links`}>
                {appSuite.HEADER_MAPPING[header].map((app) =>
                  <SectionItem
                    key={`${app}_link`}
                    onMouseEnter={() => {setHelpText(HELP_TEXT[app])}}
                    onMouseLeave={() => {setHelpText(HELP_TEXT.DEFAULT)}}
                    dataSection={appSuite.APP_DATA_SECTION_MAPPING[app]}
                    app={app}
                    productTitle={appSuite.APP_NAME_MAPPING[app]}
                    enabled={isAppEnabled(app)}
                    account={account}
                    isCurrent={app === currentApp}
                    buildUrl={buildUrl}
                  />
                )}
              </div>
            )
          })}
        </section>

        <section>
          <div data-cy="navProductsHelpText" className={styles.nav__products__footer}>
              <FontAwesomeIcon
                icon={faInfoCircle}
                size={'1x'}
                color={'nimbus'}
              />
              <span>{helpText}</span>
          </div>
        </section>
      </div>
    </Dropdown>
  )
}

export default ApplicationDropdown
